import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GRID } from '@/utils/constants';
import { SearchFilters } from './SearchFilters';
import { LoopCard } from './LoopCard';
import { PackBrowser } from './PackBrowser';
import { Library, Heart, Music2, Zap, Loader2 } from 'lucide-react';
import { fetchLoops, fetchFavorites, cacheAudioUrl } from '@/api/loops';

const TABS = [
  { id: 'loops', label: 'Loops', icon: Library },
  { id: 'packs', label: 'Packs', icon: Music2 },
  { id: 'crate', label: 'Crate', icon: Heart },
];

export const LoopLibrary = ({
  collapsed = false,
  onAddTrack,
  onPreviewLoop,
  onStopPreview,
  previewingLoopId,
  onKnockEngine,
  knockInFlight = false,
  arrangementNotice,
  coachCommand,
  tempoLock,
  onReleaseTempoLock,
  onRelockTempo,
}) => {
  const [activeTab, setActiveTab] = useState('loops');
  const [loops, setLoops] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // All 8 filter dimensions
  const [searchQuery, setSearchQuery] = useState('');
  const [activeInstrument, setActiveInstrument] = useState(null);
  const [activeMood, setActiveMood] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [activeEnergy, setActiveEnergy] = useState(null);
  const [activeBpm, setActiveBpm] = useState(null);

  // Coach requests use the same local filter state as the visible controls. The lock is read
  // through a ref so releasing it never replays the last coach command (that flipped the
  // instrument back to whatever Pizzul last suggested).
  const tempoLockActiveRef = useRef(false);
  tempoLockActiveRef.current = Boolean(tempoLock?.active);
  useEffect(() => {
    if (!coachCommand) return;
    if (coachCommand.tab) setActiveTab(coachCommand.tab);
    if (Object.prototype.hasOwnProperty.call(coachCommand, 'instrument')) setActiveInstrument(coachCommand.instrument);
    if (Object.prototype.hasOwnProperty.call(coachCommand, 'bpm') && !tempoLockActiveRef.current) setActiveBpm(coachCommand.bpm);
    else if (Object.prototype.hasOwnProperty.call(coachCommand, 'instrument')) setActiveBpm(null);
  }, [coachCommand]);

  const effectiveBpm = tempoLock?.active ? tempoLock.bpm : activeBpm;
  const handleBpmChange = useCallback((nextBpm) => {
    if (tempoLock?.active) onReleaseTempoLock?.();
    setActiveBpm(nextBpm);
  }, [tempoLock?.active, onReleaseTempoLock]);
  const releaseTempoLock = useCallback(() => {
    setActiveBpm(null);
    onReleaseTempoLock?.();
  }, [onReleaseTempoLock]);

  // Fetch loops from API with filters. Each filter change can start more than one
  // request, so only the newest response may update the list: an older reply that
  // lands last (typing "120" fires for 1, 12 and 120) must never overwrite it.
  const requestSeqRef = useRef(0);
  const loadLoops = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (searchQuery) filters.q = searchQuery;
      if (activeInstrument) filters.instrument = activeInstrument;
      if (activeMood) filters.mood = activeMood;
      if (activeKey) filters.key = activeKey;
      if (activeEnergy) filters.energy = activeEnergy;
      if (effectiveBpm) filters.bpm = effectiveBpm;
      const data = await fetchLoops(filters);
      if (seq !== requestSeqRef.current) return;
      // Validate each loop has required fields
      const validated = data.map((loop) => ({
        id: loop.id || 'unknown',
        name: loop.name || 'Untitled',
        bpm: loop.bpm || 90,
        key: loop.key || '-',
        instrument_group: loop.instrument_group || 'melody',
        bars: Number(loop.bars) > 0 ? Number(loop.bars) : null,
        energy: loop.energy || 3,
        moods: loop.moods || [],
        style_tags: Array.isArray(loop.style_tags) ? loop.style_tags : [],
        pack_code: loop.pack_code || null,
        audio_url: loop.audio_url || '',
      }));
      setLoops(validated);
      // Cache audio URLs for direct playback (avoid proxy for audio)
      validated.forEach((loop) => cacheAudioUrl(loop.id, loop.audio_url));
    } catch (e) {
      if (seq !== requestSeqRef.current) return;
      console.warn('Failed to fetch loops:', e.message);
      setError('Could not load loops. Check your connection.');
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [searchQuery, activeInstrument, activeMood, activeKey, activeEnergy, effectiveBpm]);

  // Debounced search — reload loops when search query changes (with 300ms delay)
  const searchTimerRef = useRef(null);
  useEffect(() => {
    if (activeTab !== 'loops') return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => loadLoops(), 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [activeTab, loadLoops]);

  // Also reload on filter changes (immediate, not debounced for non-search)
  const prevFiltersRef = useRef('');
  useEffect(() => {
    if (activeTab !== 'loops') return;
    const key = `${activeInstrument}-${activeMood}-${activeKey}-${activeEnergy}-${effectiveBpm}`;
    if (key !== prevFiltersRef.current) {
      prevFiltersRef.current = key;
      loadLoops();
    }
  }, [activeTab, activeInstrument, activeMood, activeKey, activeEnergy, effectiveBpm, loadLoops]);

  // Load favorites
  const [favError, setFavError] = useState(null);
  useEffect(() => {
    if (activeTab === 'crate') {
      setFavError(null);
      fetchFavorites()
        .then((data) => {
          const validated = (data || []).map((loop) => ({
            id: loop.id || 'unknown', name: loop.name || 'Untitled', bpm: loop.bpm || 90,
            key: loop.key || '-', instrument_group: loop.instrument_group || 'melody',
            bars: Number(loop.bars) > 0 ? Number(loop.bars) : null, energy: loop.energy || 3, moods: loop.moods || [], style_tags: Array.isArray(loop.style_tags) ? loop.style_tags : [],
            pack_code: loop.pack_code || null, audio_url: loop.audio_url || '',
          }));
          validated.forEach((lp) => cacheAudioUrl(lp.id, lp.audio_url));
          setFavorites(validated);
        })
        .catch((e) => {
          console.warn('Favorites fetch error:', e.message);
          setFavError(e.message.includes('401') || e.message.includes('403')
            ? 'Sign in to SP2.0 to see your favorites.'
            : 'Could not load favorites.');
        });
    }
  }, [activeTab]);

  // Loops are now filtered server-side via API params (q, mood, instrument, etc.)
  const filteredLoops = loops;

  if (collapsed) return null;

  return (
    <div
      data-testid="loop-library"
      className="shrink-0 flex flex-col border-r overflow-hidden"
      style={{ width: GRID.LIBRARY_WIDTH, background: 'var(--bg)', borderColor: 'var(--border)' }}
    >
      {/* Header */}
      <div className="flex items-center px-3 shrink-0 border-b" style={{ height: 40, borderColor: 'var(--border)' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Loop Library</span>
      </div>

      {/* Tabs */}
      <div className="flex shrink-0 border-b" style={{ borderColor: 'var(--border)' }}>
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 transition-all"
              style={{
                fontSize: 11, fontWeight: 600,
                color: activeTab === tab.id ? 'var(--green)' : 'var(--text-dim)',
                borderBottom: activeTab === tab.id ? '2px solid var(--green)' : '2px solid transparent',
              }}
            >
              <Icon size={13} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'loops' && (
          <>
            <SearchFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              activeInstrument={activeInstrument}
              onInstrumentChange={setActiveInstrument}
              activeMood={activeMood}
              onMoodChange={setActiveMood}
              activeKey={activeKey}
              onKeyChange={setActiveKey}
              activeEnergy={activeEnergy}
              onEnergyChange={setActiveEnergy}
              activeBpm={effectiveBpm}
              onBpmChange={handleBpmChange}
              tempoLock={{ ...tempoLock, hasLibraryLoops: Boolean(tempoLock?.hasLibraryLoops) }}
              onReleaseTempoLock={releaseTempoLock}
              onRelockTempo={onRelockTempo}
            />
            <div className="flex flex-col gap-1.5 px-3 pb-3">
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Loops</div>
              {!loading && !error && tempoLock?.active && filteredLoops.length < 3 && (
                <div data-testid="thin-tempo-results" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                  Only {filteredLoops.length} {activeInstrument || 'loops'} at {tempoLock.bpm} BPM. Tap &quot;Show all tempos&quot; for more (they may not line up).
                </div>
              )}
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-dim)' }} />
                </div>
              )}
              {error && !loading && (
                <div className="flex flex-col items-center gap-2 py-8 text-center">
                  <span style={{ fontSize: 12, color: 'var(--pink)' }}>{error}</span>
                  <button onClick={loadLoops} style={{ fontSize: 11, fontWeight: 600, color: 'var(--green)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 22, padding: '4px 14px', cursor: 'pointer' }}>
                    Retry
                  </button>
                </div>
              )}
              {!loading && !error && filteredLoops.map((loop) => (
                <LoopCard
                  key={loop.id}
                  loop={loop}
                  isPreviewPlaying={previewingLoopId}
                  onPreview={onPreviewLoop}
                  onStopPreview={onStopPreview}
                  onAddTrack={onAddTrack}
                />
              ))}
              {!loading && !error && filteredLoops.length === 0 && (
                <div className="py-8 text-center" style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                  No loops found. Try different filters.
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'packs' && (
          <PackBrowser onAddTrack={onAddTrack} onPreviewLoop={onPreviewLoop} onStopPreview={onStopPreview} previewingLoopId={previewingLoopId} />
        )}

        {activeTab === 'crate' && (
          <div className="flex flex-col">
            {favError && (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <Heart size={24} style={{ color: 'var(--text-dim)', marginBottom: 8 }} />
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{favError}</span>
              </div>
            )}
            {!favError && favorites.length > 0 ? (
              <div className="flex flex-col gap-1.5 px-3 py-2">
                {favorites.map((loop) => (
                  <LoopCard
                    key={loop.id}
                    loop={loop}
                    isPreviewPlaying={previewingLoopId}
                    onPreview={onPreviewLoop}
                    onStopPreview={onStopPreview}
                    onAddTrack={onAddTrack}
                  />
                ))}
              </div>
            ) : !favError && (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <Heart size={24} style={{ color: 'var(--text-dim)', marginBottom: 8 }} />
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                  Your favorites from SP2.0 Crate will appear here
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Knock Engine Button — Section 9.3 */}
      <div className="shrink-0 px-3 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
        <button
          data-testid="knock-engine-btn"
          onClick={onKnockEngine}
          disabled={knockInFlight}
          className="flex items-center justify-center gap-2 w-full transition-all"
          style={{
            height: 32, borderRadius: 22,
            fontSize: 11, fontWeight: 600,
            background: 'var(--green-bass)', color: 'var(--bg)',
            border: 'none',
          }}
        >
          <Zap size={13} />
          {knockInFlight ? 'Generating Knock Bass…' : 'Add Knock Engine Bass'}
        </button>
        {arrangementNotice && <div data-testid="arrangement-notice" style={{ marginTop: 6, fontSize: 11, color: arrangementNotice.startsWith('Could not') ? 'var(--pink)' : 'var(--green)' }}>{arrangementNotice}</div>}
      </div>
    </div>
  );
};

export default LoopLibrary;
